import { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider, AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from '@azure/msal-react';
import { jwtDecode } from "jwt-decode";
import DashboardPage from './DashboardPage';
import AdminPage from './AdminPage';
import SupportPage from './SupportPage';
import NetworkDemoPage from './components/visualizer/NetworkDemoPage';
import NetworkAdminPage from './NetworkAdminPage';
import AuditLogs from './AuditLogs';
import HomePage from './HomePage';
import LocationPage from './LocationPage';
import Login from './components/Login';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import { msalConfig, loginRequest } from './authConfig';

// --- STYLES & CONFIG ---

const GlobalStyles = () => (
    <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

        body, #root, .flex-1, main {
            box-shadow: none !important;
        }
        body {
            font-family: 'Inter', sans-serif;
            background: #000000 !important;
            background-image:
                radial-gradient(ellipse at 50% -20%, rgba(29, 78, 216, 0.15) 0%, rgba(124, 58, 237, 0.08) 40%, transparent 80%) !important;
            background-size: 100% 100% !important;
            background-attachment: fixed !important;
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
            position: relative;
        }
        body::after {
            display: none;
        }

        /* Updated Scrollbar */
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb {
            background-color: rgba(255, 255, 255, 0.1);
            border-radius: 10px;
        }
        ::-webkit-scrollbar-thumb:hover { background-color: rgba(255, 255, 255, 0.2); }

        /* ... keeping animations ... */
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeInUp { animation: fadeInUp 0.5s ease-out forwards; opacity: 0; }

        @keyframes pulse-loading { 0%, 100% { opacity: 0.5; } 50% { opacity: 1; } }
        .animate-pulse-loading { animation: pulse-loading 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite; }

        @keyframes pulse { 0%, 100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.05); opacity: 0.8; } }
        .animate-pulse-slow { animation: pulse 3s ease-in-out infinite; }
        @keyframes shimmer { 100% { transform: translateX(100%); } }

        @keyframes pulse-critical {
            0% { box-shadow: inset 0 0 4px #FF3B30, 0 0 8px #FF3B30, 0 0 16px rgba(255, 59, 48, 0.2); }
            100% { box-shadow: inset 0 0 4px #FF3B30, 0 0 12px #FF3B30, 0 0 24px rgba(255, 59, 48, 0.8); }
        }

        /* Focus-UI Glass Chassis (Cards & Map) */
        .apple-login-glass {
            background: rgba(255, 255, 255, 0.03) !important;
            backdrop-filter: blur(60px) saturate(210%) !important;
            -webkit-backdrop-filter: blur(60px) saturate(210%) !important;
            border: 1px solid rgba(255, 255, 255, 0.12) !important;
            box-shadow: none !important;
            position: relative;
        }
        .apple-login-glass::before {
            content: '';
            position: absolute;
            inset: 0;
            padding: 1px;
            border-radius: inherit;
            background: linear-gradient(135deg, rgba(0, 255, 255, 0.2) 0%, rgba(255, 0, 255, 0.15) 10%, transparent 30%);
            -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
            mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
            -webkit-mask-composite: xor;
            mask-composite: exclude;
            pointer-events: none;
            z-index: 10;
        }

        /* Volumetric Header Glass */
        .header-glass {
            background: rgba(255, 255, 255, 0.03) !important;
            backdrop-filter: blur(60px) saturate(210%) !important;
            -webkit-backdrop-filter: blur(60px) saturate(210%) !important;
            border-bottom: 1px solid rgba(255, 255, 255, 0.12) !important;
            box-shadow: none !important;
        }

        /* Solid State Map Pins */
        .map-pin-diode {
            display: block !important;
            width: 14px !important;
            height: 14px !important;
            border-radius: 50% !important;
            border: 2.5px solid #FFFFFF !important;
            z-index: 9999 !important;
            opacity: 1 !important;
            filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.9));
            box-shadow: 0 0 4px rgba(255, 255, 255, 0.4) !important;
        }



        /* Premium Glass Effect Class */
        .glass-panel {
            background: rgba(10, 10, 10, 0.6);
            backdrop-filter: blur(20px);
            -webkit-backdrop-filter: blur(20px);
            border: 1px solid rgba(255, 255, 255, 0.05);
            box-shadow: 0 4px 24px rgba(0, 0, 0, 0.4);
        }
    `}</style>
);

const msalInstance = new PublicClientApplication(msalConfig);

const ProtectedAdminRoute = ({ children, isAdmin }) => {
    if (!isAdmin) {
        return <Navigate to="/dashboard" replace />;
    }
    return children;
};

// NEW: For highly restricted paths like Audit Logs and Rack Builder
const ProtectedSuperAdminRoute = ({ children, isSuperAdmin }) => {
    if (!isSuperAdmin) {
        return <Navigate to="/dashboard" replace />;
    }
    return children;
};

const AppContent = () => {
    const { instance } = useMsal();
    const location = useLocation();
    const [isAdmin, setIsAdmin] = useState(false);
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);
    const [currentUserEmail, setCurrentUserEmail] = useState('');
    const [isAuthLoading, setIsAuthLoading] = useState(true);
    // Preview mode: skip intro, no layout shell (used by HomePage iframe tiles)
    const searchParams = new URLSearchParams(location.search);
    const isPreviewMode = searchParams.get('preview') === 'true';

    useEffect(() => {
        const checkAdminStatus = async () => {
            try {
                const accounts = instance.getAllAccounts();
                if (accounts.length > 0) {
                    const response = await instance.acquireTokenSilent({ ...loginRequest, account: accounts[0] });
                    const decodedToken = jwtDecode(response.accessToken);

                    // MSAL sometimes doesn't put the email in the token claims,
                    // but it usually has it on the account object itself
                    const userEmail = (accounts[0].username || decodedToken.preferred_username || decodedToken.upn || '').toLowerCase();

                    setCurrentUserEmail(userEmail);

                    const configuredSuperAdmin = (process.env.REACT_APP_ADMIN_EMAIL || '').toLowerCase();
                    const configuredAdminDomain = configuredSuperAdmin.includes('@')
                        ? configuredSuperAdmin.split('@')[1]
                        : '';
                    const isConfiguredDomainUser = configuredAdminDomain
                        ? userEmail.endsWith(`@${configuredAdminDomain}`)
                        : false;

                    if (userEmail && isConfiguredDomainUser) {
                        setIsAdmin(true);
                    }
                    if (userEmail && configuredSuperAdmin && userEmail === configuredSuperAdmin) {
                        setIsSuperAdmin(true);
                    }
                }
            } catch (err) { console.error(err); } finally { setIsAuthLoading(false); }
        };
        checkAdminStatus();
    }, [instance]);

    const handleLogout = () => { instance.logoutRedirect({ postLogoutRedirectUri: "/" }); };

    // Preview mode: skip intro, render pages without Layout for iframe embedding
    if (isPreviewMode) {
        if (isAuthLoading) return null;
        return (
            <>
                <GlobalStyles />
                <div
                    data-testid="preview-render-surface"
                    className="h-screen w-screen overflow-hidden bg-black"
                    style={{ backgroundColor: '#000000' }}
                >
                    <Routes>
                        <Route path="/dashboard" element={<HomePage isAdmin={isAdmin} isSuperAdmin={isSuperAdmin} userEmail={currentUserEmail} />} />
                        <Route path="/overview" element={<DashboardPage isPreviewMode={isPreviewMode} />} />
                        <Route path="/admin" element={<AdminPage isPreviewMode={isPreviewMode} />} />
                        <Route path="/admin/audit-logs" element={<AuditLogs />} />
                        <Route path="/support" element={<SupportPage isPreviewMode={isPreviewMode} />} />
                        <Route path="/locations/:locationId" element={<LocationPage isAdmin={isAdmin} isSuperAdmin={isSuperAdmin} />} />
                        <Route path="*" element={null} />
                    </Routes>
                </div>
            </>
        );
    }

    if (isAuthLoading) return <GlobalStyles />;

    const dashboardLaunchpad = (
        <ErrorBoundary label="Dashboard">
            <HomePage isAdmin={isAdmin} isSuperAdmin={isSuperAdmin} userEmail={currentUserEmail} />
        </ErrorBoundary>
    );

    if (location.pathname === '/' || location.pathname === '/dashboard') {
        return (
            <>
                <GlobalStyles />
                <Routes>
                    <Route path="/" element={<Navigate to="/dashboard" replace />} />
                    <Route path="/dashboard" element={dashboardLaunchpad} />
                    <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Routes>
            </>
        );
    }

    return (
        <>
            <GlobalStyles />
            <Layout isAdmin={isAdmin} isSuperAdmin={isSuperAdmin} handleLogout={handleLogout} userEmail={currentUserEmail}>
                <Routes>
                    <Route path="/" element={<Navigate to="/dashboard" replace />} />
                    <Route path="/dashboard" element={dashboardLaunchpad} />
                    <Route
                        path="/overview"
                        element={
                            <ErrorBoundary label="Technology & Site Overview">
                                <DashboardPage />
                            </ErrorBoundary>
                        }
                    />
                    <Route
                        path="/admin"
                        element={
                            <ProtectedAdminRoute isAdmin={isAdmin}>
                                <ErrorBoundary label="Operations">
                                    <AdminPage />
                                </ErrorBoundary>
                            </ProtectedAdminRoute>
                        }
                    />
                    <Route
                        path="/admin/audit-logs"
                        element={
                            <ProtectedSuperAdminRoute isSuperAdmin={isSuperAdmin}>
                                <ErrorBoundary label="Audit Logs">
                                    <AuditLogs />
                                </ErrorBoundary>
                            </ProtectedSuperAdminRoute>
                        }
                    />
                    <Route
                        path="/support"
                        element={
                            <ErrorBoundary label="Support">
                                <SupportPage />
                            </ErrorBoundary>
                        }
                    />
                    <Route
                        path="/network-demo"
                        element={
                            <ProtectedSuperAdminRoute isSuperAdmin={isSuperAdmin}>
                                <ErrorBoundary label="Network Demo">
                                    <NetworkDemoPage />
                                </ErrorBoundary>
                            </ProtectedSuperAdminRoute>
                        }
                    />
                    <Route
                        path="/network-admin"
                        element={
                            <ProtectedSuperAdminRoute isSuperAdmin={isSuperAdmin}>
                                <ErrorBoundary label="Network Admin">
                                    <NetworkAdminPage />
                                </ErrorBoundary>
                            </ProtectedSuperAdminRoute>
                        }
                    />
                    <Route
                        path="/locations/:locationId"
                        element={
                            <ErrorBoundary label="Location">
                                <LocationPage isAdmin={isAdmin} isSuperAdmin={isSuperAdmin} />
                            </ErrorBoundary>
                        }
                    />
                    <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Routes>
            </Layout>
        </>
    );
};

// Main App component that provides the MSAL context
export default function App() {
    return (
        <MsalProvider instance={msalInstance}>
            <AuthenticatedTemplate>
                <AppContent />
            </AuthenticatedTemplate>
            <UnauthenticatedTemplate>
                <Login />
            </UnauthenticatedTemplate>
        </MsalProvider>
    );
}
